import React from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardMedia from '@material-ui/core/CardMedia';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  heroCard: {
    cursor: 'pointer',
    '&:hover': {
      boxShadow: theme.shadows[8],
    },
  },
  media: {
    height: 300,
    backgroundSize: 'contain',
  },
  publisherBadge: {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    padding: '4px 8px',
    borderRadius: '4px',
    display: 'inline-block',
    marginBottom: theme.spacing(1),
  },
  description: {
    maxHeight: '100px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '& p': {
      margin: 0,
    },
    '& a': {
      color: theme.palette.primary.main,
    },
  },
}));

// Fallback image for heroes without thumbnails
const PLACEHOLDER_IMAGE = 'https://via.placeholder.com/300x450.jpg';

const HeroSelectionDialog = ({ open, heroes, onSelect, onClose }) => {
  const classes = useStyles();

  const handleSelect = (hero) => {
    onSelect(hero);
    // Do not call onClose here — onSelect (handleHeroSelect) already closes the
    // dialog via setShowDialog(false).  Calling onClose would also invoke
    // handleDialogClose which creates a duplicate custom entry that overwrites
    // the Comic Vine hero data (losing the image and comicVineId).
  };

  // Strip HTML tags and convert to plain text (safer than dangerouslySetInnerHTML)
  const stripHtmlTags = (html) => {
    if (!html) return '';
    // Use DOMParser for safer HTML parsing
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
    >
      <DialogTitle>Select a Hero</DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          {heroes.map((hero) => {
            // Safely get thumbnail URL with fallback
            const hasThumbnail = hero.thumbnail?.path && hero.thumbnail?.extension;
            const thumbnailUrl = hasThumbnail 
              ? `${hero.thumbnail.path}.${hero.thumbnail.extension}`
              : PLACEHOLDER_IMAGE;
            
            return (
              <Grid item xs={12} sm={6} md={4} key={hero.id}>
                <Card 
                  className={classes.heroCard}
                  onClick={() => handleSelect(hero)}
                >
                  <CardMedia
                    className={classes.media}
                    image={thumbnailUrl}
                    title={hero.name}
                  />
                  <CardContent>
                    <Typography gutterBottom variant="h6" component="h2">
                      {hero.name}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      className={classes.publisherBadge}
                    >
                      {hero.publisher || 'Unknown'}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="textSecondary" 
                      component="p"
                      className={classes.description}
                    >
                      {stripHtmlTags(hero.description)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HeroSelectionDialog;
